import { ReactNode, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  alpha,
  ImageList,
  ImageListItem,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  LocationOn as LocationIcon,
  Bed as BedIcon,
  Bathtub as BathtubIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  CameraAlt as CameraIcon,
  Visibility as ViewIcon,
  Settings as SettingsIcon,
  Warning as WarningIcon,
  Straighten as AreaIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import CustomAppBar from '../../../components/shared/CustomAppBar';

// Datos de ejemplo extendidos para la propiedad individual
const getSinglePropertyData = (id: string) => {
  const properties = {
    '1': {
      id: 1,
      name: 'Casa Familiar San Pedro',
      location: 'San Pedro, San José',
      address: 'Del Mall San Pedro, 200m sur, 100m este',
      type: 'Casa',
      bedrooms: 3,
      bathrooms: 2,
      area: '180 m²',
      lotSize: '250 m²',
      monthlyPrice: 650000,
      currency: 'CRC',
      paymentDate: '2024-01-15',
      status: 'occupied',
      photos: [
        'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=400&h=300&fit=crop',
        'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=400&h=300&fit=crop',
        'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400&h=300&fit=crop',
        'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=400&h=300&fit=crop'
      ],
      services: {
        water: { enabled: true, paymentDate: '2024-01-10', niseCode: '12345678' },
        electricity: { enabled: true, paymentDate: '2024-01-12', niseCode: '87654321' },
        cable: { enabled: false, paymentDate: '', niseCode: '' },
        internet: { enabled: true, paymentDate: '2024-01-08', niseCode: '11223344' },
        gas: { enabled: true, paymentDate: '2024-01-05', niseCode: '55667788' },
        security: { enabled: false, paymentDate: '', niseCode: '' }
      },
      tenant: {
        name: 'Carlos Rodríguez Méndez',
        cedula: '1-1234-5678',
        email: 'carlos.rodriguez@email.com',
        phone: '+506 8888-1234',
        profession: 'Contador Público',
        entryDate: '2024-01-15',
        emergencyContact: 'María Rodríguez - +506 7777-9999',
        notes: 'Inquilino responsable, siempre paga a tiempo. Familia de 4 personas.'
      },
      lastPayment: {
        date: '2024-11-15',
        amount: 650000,
        status: 'paid'
      }
    }
  };
  
  return properties[id as keyof typeof properties] || null;
};

const getStatusInfo = (status: string) => {
  switch (status) {
    case 'available':
      return { label: 'Disponible', color: '#4CAF50', bg: alpha('#4CAF50', 0.1) };
    case 'occupied':
      return { label: 'Ocupado', color: '#FF9800', bg: alpha('#FF9800', 0.1) };
    case 'maintenance':
      return { label: 'Mantenimiento', color: '#F44336', bg: alpha('#F44336', 0.1) };
    default:
      return { label: 'Desconocido', color: '#9E9E9E', bg: alpha('#9E9E9E', 0.1) };
  }
};

interface ServiceItemProps {
  title: string;
  icon: ReactNode;
  serviceData?: {
    paymentDate: string;
    niseCode: string;
  };
  enabled?: boolean;
}

const ServiceItem = ({ title, icon, serviceData, enabled = false }: ServiceItemProps) => {
  return (
    <ListItem sx={{ px: 0 }}>
      <ListItemIcon>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          width: 40,
          height: 40,
          borderRadius: '50%',
          backgroundColor: enabled ? alpha('#4CAF50', 0.1) : alpha('#F44336', 0.1)
        }}>
          <Typography sx={{ fontSize: '1.2rem' }}>{icon}</Typography>
        </Box>
      </ListItemIcon>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body1" fontWeight="medium">
              {title}
            </Typography>
            {enabled ? (
              <CheckIcon sx={{ color: '#4CAF50', fontSize: 18 }} />
            ) : (
              <CloseIcon sx={{ color: '#F44336', fontSize: 18 }} />
            )}
          </Box>
        }
        secondary={
          enabled && serviceData ? (
            <Box sx={{ mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Fecha pago: {serviceData.paymentDate || 'No especificado'}
              </Typography>
              <br />
              <Typography variant="caption" color="text.secondary">
                NISE: {serviceData.niseCode || 'No especificado'}
              </Typography>
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">
              No incluido
            </Typography>
          )
        }
      />
    </ListItem>
  );
};

const SinglePropertyDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');

  const propertyData = getSinglePropertyData(id!);

  if (!propertyData) {
    return (
      <>
        <CustomAppBar />
        <Box sx={{ textAlign: 'center', p: 4, mt: '10vh' }}>
          <Typography variant="h5">Propiedad no encontrada</Typography>
          <Button onClick={() => navigate('/properties-inventory')}>
            Volver al inicio
          </Button>
        </Box>
      </>
    );
  }

  const statusInfo = getStatusInfo(propertyData.status);

  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setImageDialogOpen(true);
  };

  const handleEditSave = () => {
    setEditDialogOpen(false);
    // Aquí iría la lógica para guardar los cambios
  };

  const handleDelete = () => {
    setDeleteDialogOpen(false);
    // Aquí iría la lógica para eliminar la propiedad
  };

  return (
    <>
      <CustomAppBar />
      <Box sx={{ backgroundColor: 'white', minHeight: '100vh', mt: '10vh' }}>
        {/* Header */}
        <Box sx={{ 
          p: 3, 
          borderBottom: '1px solid #E0E0E0',
          backgroundColor: 'white',
          mb: 3 
        }}>
          <Container maxWidth="lg">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  onClick={() => navigate('/properties-inventory')}
                  sx={{ 
                    mr: 2,
                    backgroundColor: alpha('#4DB6AC', 0.1),
                    '&:hover': {
                      backgroundColor: alpha('#4DB6AC', 0.2),
                    }
                  }}
                >
                  <ArrowBackIcon sx={{ color: '#4DB6AC' }} />
                </IconButton>
                <HomeIcon sx={{ mr: 2, color: '#4DB6AC' }} />
                <Box>
                  <Typography variant="h5" component="div" fontWeight="bold" color="#2E7D7A">
                    {propertyData.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {propertyData.address}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setEditDialogOpen(true)}
                  sx={{
                    borderColor: '#4DB6AC',
                    color: '#4DB6AC',
                    '&:hover': {
                      borderColor: '#26A69A',
                      backgroundColor: alpha('#4DB6AC', 0.1)
                    }
                  }}
                >
                  Editar
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteDialogOpen(true)}
                  sx={{
                    borderColor: '#F44336',
                    color: '#F44336',
                    '&:hover': {
                      borderColor: '#D32F2F',
                      backgroundColor: alpha('#F44336', 0.1)
                    }
                  }}
                >
                  Eliminar
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>

        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Columna izquierda - Información principal */}
            <Grid item xs={12} md={8}>
              {/* Información básica */}
              <Card sx={{ mb: 4 }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Typography variant="h6" fontWeight="bold" color="#2E7D7A">
                      Información General
                    </Typography>
                    <Chip
                      label={statusInfo.label}
                      sx={{
                        backgroundColor: statusInfo.bg,
                        color: statusInfo.color,
                        fontWeight: 'bold'
                      }}
                    />
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <HomeIcon sx={{ color: '#4DB6AC', mr: 2 }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">Tipo</Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {propertyData.type}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <MoneyIcon sx={{ color: '#4DB6AC', mr: 2 }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">Precio Mensual</Typography>
                          <Typography variant="body1" fontWeight="medium">
                            ₡{propertyData.monthlyPrice.toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <BedIcon sx={{ color: '#4DB6AC', mr: 2 }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">Habitaciones</Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {propertyData.bedrooms} habitaciones
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <BathtubIcon sx={{ color: '#4DB6AC', mr: 2 }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">Baños</Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {propertyData.bathrooms} baños
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <AreaIcon sx={{ color: '#4DB6AC', mr: 2 }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">Área Construcción</Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {propertyData.area}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <LocationIcon sx={{ color: '#4DB6AC', mr: 2 }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">Lote</Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {propertyData.lotSize}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <CalendarIcon sx={{ color: '#4DB6AC', mr: 2 }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">Fecha de Pago</Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {new Date(propertyData.paymentDate).toLocaleDateString('es-CR')}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <MoneyIcon sx={{ color: '#4DB6AC', mr: 2 }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">Último Pago</Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {new Date(propertyData.lastPayment.date).toLocaleDateString('es-CR')}
                          </Typography>
                          <Chip 
                            label="Pagado" 
                            size="small"
                            sx={{ 
                              backgroundColor: alpha('#4CAF50', 0.1),
                              color: '#388E3C',
                              fontSize: '0.7rem'
                            }}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Servicios */}
              <Card sx={{ mb: 4 }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" fontWeight="bold" color="#2E7D7A" sx={{ mb: 3 }}>
                    Servicios de la Propiedad
                  </Typography>

                  <List>
                    <ServiceItem
                      title="Agua"
                      icon="💧"
                      serviceData={propertyData.services.water}
                      enabled={propertyData.services.water.enabled}
                    />
                    <ServiceItem
                      title="Electricidad"
                      icon="⚡"
                      serviceData={propertyData.services.electricity}
                      enabled={propertyData.services.electricity.enabled}
                    />
                    <ServiceItem
                      title="Gas"
                      icon="🔥"
                      serviceData={propertyData.services.gas}
                      enabled={propertyData.services.gas.enabled}
                    />
                    <ServiceItem
                      title="Cable TV"
                      icon="📺"
                      serviceData={propertyData.services.cable}
                      enabled={propertyData.services.cable.enabled}
                    />
                    <ServiceItem
                      title="Internet"
                      icon="🌐"
                      serviceData={propertyData.services.internet}
                      enabled={propertyData.services.internet.enabled}
                    />
                    <ServiceItem
                      title="Seguridad"
                      icon="🛡️"
                      serviceData={propertyData.services.security}
                      enabled={propertyData.services.security.enabled}
                    />
                  </List>
                </CardContent>
              </Card>

              {/* Galería de fotos */}
              <Card>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Typography variant="h6" fontWeight="bold" color="#2E7D7A">
                      Galería ({propertyData.photos.length} fotos)
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<CameraIcon />}
                      size="small"
                      sx={{
                        borderColor: '#4DB6AC',
                        color: '#4DB6AC'
                      }}
                    >
                      Agregar Fotos
                    </Button>
                  </Box>

                  <ImageList cols={2} gap={16}>
                    {propertyData.photos.map((photo, index) => (
                      <ImageListItem key={index}>
                        <Box
                          sx={{
                            position: 'relative',
                            cursor: 'pointer',
                            borderRadius: 2,
                            overflow: 'hidden',
                            '&:hover .overlay': {
                              opacity: 1
                            }
                          }}
                          onClick={() => handleImageClick(photo)}
                        >
                          <img
                            src={photo}
                            alt={`${propertyData.name} - ${index + 1}`}
                            style={{
                              width: '100%',
                              height: 200,
                              objectFit: 'cover'
                            }}
                          />
                          <Box
                            className="overlay"
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              backgroundColor: 'rgba(0,0,0,0.5)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              opacity: 0,
                              transition: 'opacity 0.3s ease'
                            }}
                          >
                            <ViewIcon sx={{ color: 'white', fontSize: 40 }} />
                          </Box>
                        </Box>
                      </ImageListItem>
                    ))}
                  </ImageList>
                </CardContent>
              </Card>
            </Grid>

            {/* Columna derecha - Inquilino o estado */}
            <Grid item xs={12} md={4}>
              {propertyData.status === 'occupied' && propertyData.tenant ? (
                <Card sx={{ position: 'sticky', top: 20 }}>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                      <Typography variant="h6" fontWeight="bold" color="#2E7D7A">
                        Inquilino Actual
                      </Typography>
                      <IconButton size="small">
                        <SettingsIcon sx={{ color: '#4DB6AC' }} />
                      </IconButton>
                    </Box>

                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                      <Avatar
                        sx={{
                          width: 80,
                          height: 80,
                          backgroundColor: '#4DB6AC',
                          fontSize: '2rem',
                          mx: 'auto',
                          mb: 2
                        }}
                      >
                        {propertyData.tenant.name.charAt(0)}
                      </Avatar>
                      <Typography variant="h6" fontWeight="bold">
                        {propertyData.tenant.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {propertyData.tenant.profession}
                      </Typography>
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <PersonIcon sx={{ color: '#4DB6AC', mr: 2, fontSize: 20 }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary">Cédula</Typography>
                          <Typography variant="body2">{propertyData.tenant.cedula}</Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <PhoneIcon sx={{ color: '#4DB6AC', mr: 2, fontSize: 20 }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary">Teléfono</Typography>
                          <Typography variant="body2">{propertyData.tenant.phone}</Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <EmailIcon sx={{ color: '#4DB6AC', mr: 2, fontSize: 20 }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary">Email</Typography>
                          <Typography variant="body2">{propertyData.tenant.email}</Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <CalendarIcon sx={{ color: '#4DB6AC', mr: 2, fontSize: 20 }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary">Fecha de Ingreso</Typography>
                          <Typography variant="body2">
                            {new Date(propertyData.tenant.entryDate).toLocaleDateString('es-CR')}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {propertyData.tenant.emergencyContact && (
                      <>
                        <Divider sx={{ mb: 2 }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary">Contacto de Emergencia</Typography>
                          <Typography variant="body2">{propertyData.tenant.emergencyContact}</Typography>
                        </Box>
                      </>
                    )}

                    {propertyData.tenant.notes && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary">Notas</Typography>
                          <Typography variant="body2">{propertyData.tenant.notes}</Typography>
                        </Box>
                      </>
                    )}

                    <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{
                          borderColor: '#4DB6AC',
                          color: '#4DB6AC'
                        }}
                      >
                        Contactar
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{
                          borderColor: '#FF9800',
                          color: '#FF9800'
                        }}
                      >
                        Ver Historial
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ) : (
                <Card sx={{ position: 'sticky', top: 20 }}>
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6" fontWeight="bold" color="#2E7D7A" sx={{ mb: 2 }}>
                      Propiedad {statusInfo.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {propertyData.status === 'available' 
                        ? 'Esta propiedad está disponible para alquilar'
                        : 'Esta propiedad está en mantenimiento'
                      }
                    </Typography>
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{
                        backgroundColor: '#4DB6AC',
                        '&:hover': {
                          backgroundColor: '#26A69A'
                        }
                      }}
                    >
                      {propertyData.status === 'available' ? 'Asignar Inquilino' : 'Cambiar Estado'}
                    </Button>
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
        </Container>

        {/* Dialogs igual que en el componente de apartamentos */}
        <Dialog
          open={imageDialogOpen}
          onClose={() => setImageDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogContent sx={{ p: 0 }}>
            <img
              src={selectedImage}
              alt="Vista completa"
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '80vh',
                objectFit: 'contain'
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setImageDialogOpen(false)}>Cerrar</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Editar {propertyData.name}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nombre de la Propiedad"
                  defaultValue={propertyData.name}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    defaultValue={propertyData.type}
                    label="Tipo"
                  >
                    <MenuItem value="Casa">Casa</MenuItem>
                    <MenuItem value="Apartamento">Apartamento</MenuItem>
                    <MenuItem value="Condominio">Condominio</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Precio Mensual"
                  type="number"
                  defaultValue={propertyData.monthlyPrice}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Estado</InputLabel>
                  <Select
                    defaultValue={propertyData.status}
                    label="Estado"
                  >
                    <MenuItem value="available">Disponible</MenuItem>
                    <MenuItem value="occupied">Ocupado</MenuItem>
                    <MenuItem value="maintenance">Mantenimiento</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleEditSave} variant="contained" sx={{ backgroundColor: '#4DB6AC' }}>
              Guardar Cambios
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogContent sx={{ textAlign: 'center', p: 4 }}>
            <WarningIcon sx={{ fontSize: 60, color: '#FF9800', mb: 2 }} />
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              ¿Eliminar Propiedad?
            </Typography>
            <Typography color="text.secondary">
              Esta acción no se puede deshacer. Se eliminará toda la información de {propertyData.name}.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleDelete} variant="contained" color="error">
              Eliminar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default SinglePropertyDetailsPage;